/**
 *
 * use island:name="name" to dynamically load a script based on a condition from the window theme.scripts object (see snippets/global-theme-variables.liquid)
 * available conditions:
 * - island:visible: load a script when the element is visible in the viewport
 * - island:event: load a script when the element triggers an event
 * - island:idle: load a script when the browser is idle
 * - island:media: load a script when the media query matches
 *
 * this differs from Enterprise's native initLazyScript in that it delays the loading of the script and not only the execution
 *
 **/

class TitusRevive {
  exclude = {
    predictiveSearch: () =>
      typeof window.Searchanise === "undefined" &&
      typeof window.SearchaniseIncluded === "undefined",
    predictiveSearchStyle: () =>
      typeof window.Searchanise === "undefined" &&
      typeof window.SearchaniseIncluded === "undefined"
  };

  bypass = {};

  /**
   * sometimes we need to check additional stuff whether or not to load a script
   * this is usually due to an app being present that makes the script obsolete
   * @param {string} islandName - name of the island to check against special conditions
   */
  checkExclude(islandName) {
    try {
      return window.TitusRevive.exclude[islandName]();
    } catch (e) {
      return false;
    }
  }

  /**
   * sometimes we need to check if an assets needs to bypass the revive logic
   * this can happen in case an element that should trigger the revive logic is disabled
   * @param {string} islandName - name of the island to check against special conditions
   */
  checkBypass(islandName) {
    try {
      return window.TitusRevive.bypass[islandName]();
    } catch (e) {
      return false;
    }
  }

  /**
   * returns a promise that resolves when a medias query matches
   * @param {string} query - Media query to match.
   * @returns {Promise}
   */
  media({ query }) {
    const mediaQuery = window.matchMedia(query);
    return new Promise(function (resolve) {
      if (mediaQuery.matches) {
        resolve(true);
      } else {
        mediaQuery.addEventListener("change", resolve, { once: true });
      }
    });
  }

  /**
   * returns a promise that resolves when the element scrolls into the viewport
   * @param {object} element - DOM element to observe.
   * @returns {Promise}
   */
  visible({ element, options }) {
    options = options || {};
    return new Promise(function (resolve) {
      const observer = new window.IntersectionObserver(function (entries) {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.disconnect();
            resolve(true);
            break;
          }
        }
      }, options);
      observer.observe(element);
    });
  }

  /**
   * returns a promise that resolves when the main thread is idle
   * @returns {Promise}
   */
  idle({ options } = { options: {} }) {
    options = options || {};
    return new Promise(function (resolve) {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(resolve, options);
      } else {
        setTimeout(resolve, 200);
      }
    });
  }

  /**
   * returns a promise that resolves when a specific event is triggered on a specific element
   * @param {object} element - DOM element to observe.
   * @param {string} event - Event to listen for.
   * @returns {Promise}
   */
  trigger({ element, event, options }) {
    options = options || {};
    return new Promise(function (resolve) {
      element.addEventListener(event, resolve, Object.assign(options, { once: true }));
    });
  }

  /**
   * adds a script to the head of the document
   * @param {string} src - The source of the script
   * @returns {Promise}
   */
  addScriptToHead({ src }) {
    // add script to head

    return new Promise(function (resolve, reject) {
      const head = document.getElementsByTagName("head")[0];
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      head.appendChild(script);
    });
  }

  /**
   * adds a style to the head of the document
   * @param {string} src - The source of the style
   * @returns {Promise}
   */
  addStyleToHead({ src }) {
    // add style to head
    return new Promise(function (resolve, reject) {
      const head = document.getElementsByTagName("head")[0];
      const styleElement = document.createElement("link");
      styleElement.rel = "stylesheet";
      styleElement.type = "text/css";
      styleElement.media = "all";
      styleElement.href = src;
      styleElement.onload = resolve;
      styleElement.onerror = reject;
      head.appendChild(styleElement);
    });
  }
}

window.TitusRevive = new TitusRevive();

(function titusReviveAuto() {
  const observer = new window.MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i++) {
      const { addedNodes } = mutations[i];
      for (let j = 0; j < addedNodes.length; j++) {
        const node = addedNodes[j];
        if (node.nodeType === 1) dfs(node);
      }
    }
  });

  function dfs(node) {
    const hasIslandttribute = node.hasAttribute("island:name");
    const islandNames = node.getAttribute("island:name");
    const islandOptions = JSON.parse(node.getAttribute("island:options")) || {};

    const inject = (path) => {
      const func = path.includes(".css") ? "addStyleToHead" : "addScriptToHead";
      window.TitusRevive[func]({ src: path }).catch(() => {
        // if script loading fails, try again
        setTimeout(() => dfs(node), 1000);
      });
    };

    if (hasIslandttribute) {
      islandNames.split("|").forEach(async (islandName) => {
        const potentialPath = window.theme.scripts[islandName] || window.theme.styles[islandName];

        if (potentialPath && !window.TitusRevive.checkExclude(islandName)) {
          // if bypass is set, we don't want to delay execution
          if (window.TitusRevive.checkBypass(islandName)) {
            inject(potentialPath);
          } else {
            if (node.hasAttribute("island:visible")) {
              await window.TitusRevive.visible({ element: node, options: islandOptions });
            }

            if (node.hasAttribute("island:event")) {
              await window.TitusRevive.trigger({
                element: node,
                event: node.getAttribute("island:event"),
                options: islandOptions
              });
            }

            const clientMedia = node.getAttribute("island:media");
            if (clientMedia) {
              await window.TitusRevive.media({ query: clientMedia });
            }

            if (node.hasAttribute("island:idle")) {
              await window.TitusRevive.idle({ options: islandOptions });
            }

            inject(potentialPath);
          }
        }
      });
    };

    let child = node.firstElementChild;

    while (child) {
      dfs(child);
      child = child.nextElementSibling;
    }
  }

  dfs(document.body);

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
